"use server";

import { auth } from "@/auth";
import { prisma } from "@/prisma";
import { validateParams } from "@/lib/utils";
import z from "zod";
import type { ActionResult } from "@/types/server-action";
import { canAccessDraft } from "@/lib/cat/draft-access";
import { getDraftLockHolder, releaseDraftLock } from "@/lib/cat/draft-lock";
import { logDraftDelete } from "@/lib/cat/draft-activity-log";

const deleteDraftSchema = z.object({
  draftId: z.number().int().positive(),
});

export async function deleteDraftAction(
  params: z.infer<typeof deleteDraftSchema>,
): Promise<ActionResult<{ draftId: number }>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }

    const { draftId } = validateParams(deleteDraftSchema, params);
    const draft = await prisma.draft.findUnique({
      where: { id: draftId },
    });
    if (!draft) {
      return { success: false, message: "Brouillon introuvable." };
    }

    if (!canAccessDraft(session, draft)) {
      return { success: false, message: "Accès refusé à ce brouillon." };
    }

    const lockHolder = await getDraftLockHolder(draftId);
    if (lockHolder && lockHolder.userId !== session.user.id) {
      return {
        success: false,
        message: `Brouillon en cours d'édition par ${lockHolder.userName}.`,
      };
    }

    await prisma.draft.delete({ where: { id: draftId } });
    await releaseDraftLock(draftId, session.user.id);

    await logDraftDelete({
      userId: session.user.id,
      kvps: draft.kvps,
      draftId,
      metadata: "suppression manuelle",
    });

    return { success: true, data: { draftId } };
  } catch (error) {
    console.error("[deleteDraftAction]", error);
    return { success: false, message: "Une erreur serveur est survenue." };
  }
}
